/**
 * Stripe Account Store
 * 
 * Zustand store for managing Stripe Connect account status.
 * Prevents re-renders that interfere with form filling during onboarding.
 */

import { create } from 'zustand';
import type { StripeAccountStatus } from '@/services/api/stripeConnect';

interface StripeAccountStore {
  // Account status
  status: StripeAccountStatus | null;
  loading: boolean;
  error: string | null;
  lastChecked: number | null;

  // Actions
  setStatus: (status: StripeAccountStatus | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateLastChecked: () => void;

}

// Computed selectors as separate hooks to avoid re-renders
export const useStripeAccountConnected = () => {
  return useStripeAccountStore((state) => 
    Boolean(state.status?.stripe_account_id || state.status?.connected)
  );
};

export const useStripeAccountEnabled = () => {
  return useStripeAccountStore((state) => 
    Boolean(state.status?.charges_enabled && state.status?.payouts_enabled)
  );
};

export const useStripeAccountPendingVerification = () => {
  return useStripeAccountStore((state) => 
    Boolean(state.status?.requirements?.pending_verification?.length)
  );
};

export const useStripeAccountNeedsAction = () => {
  return useStripeAccountStore((state) => 
    Boolean(
      state.status?.requirements?.currently_due?.length ||
      state.status?.requirements?.past_due?.length ||
      state.status?.disabled_reason
    )
  );
};

export const useStripeAccountNotStarted = () => {
  return useStripeAccountStore((state) => 
    !state.status?.stripe_account_id
  );
};

export const useStripeAccountStore = create<StripeAccountStore>((set) => ({
  // Initial state
  status: null,
  loading: false,
  error: null,
  lastChecked: null,

  // Actions
  setStatus: (status) => set({ status, error: null }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  updateLastChecked: () => set({ lastChecked: Date.now() }),
}));

